"use client";
import { getApiBase } from "@/configs";

import { notifySubscriptionChanged, postUnsubscribe } from "@/components/subscription-provider";
import { Button } from "@/components/ui/button";
import { UserAvatar } from "@/components/ui/user-avatar";
import PublisherConfig from "@/configs/publisher.json";
import { useTranslation } from "@/i18n";
import { isPaidAccessActive } from "@/lib/article-access";
import { getAuthToken, getAuthEmail, handleAuthError, removeAuthToken } from "@/lib/auth-cookie";
import { getMediaFullPath } from "@/lib/media";
import { cn } from "@/lib/utils";
import axios from "axios";
import dayjs from "dayjs";
import { Loader2, LogOut, Mail, UserMinus, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type HTMLAttributes } from "react";

const API_BASE = getApiBase();

function Skeleton({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div data-slot="skeleton" className={cn("animate-pulse rounded-md bg-muted", className)} {...props} />;
}

interface AccountSubscriptionPayment {
  type?: "monthly" | "annual" | string;
  price?: number;
  currency?: string;
  endedAt?: number;
  canceledAt?: number;
}

interface AccountSubscription {
  publisher: string;
  email: string;
  createdAt: number | null;
  unsubscribeToken: string | null;
  site?: {
    username?: string;
    profile?: {
      name?: string;
      avatar?: string;
    };
  };
  payment?: AccountSubscriptionPayment;
}

interface AccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSignOut?: () => void;
}

function SubscriptionAction({
  sub,
  unsubscribingToken,
  onUnsubscribe,
  onCancelPaid,
}: {
  sub: AccountSubscription;
  unsubscribingToken: string | null;
  onUnsubscribe: (item: AccountSubscription) => void;
  onCancelPaid: (item: AccountSubscription) => void;
}) {
  const { t } = useTranslation();
  const isUnsubscribing = unsubscribingToken === sub.unsubscribeToken;
  const canCancelPaid = Boolean(sub.payment && !sub.payment.canceledAt && sub.unsubscribeToken);

  if (canCancelPaid) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onCancelPaid(sub)}
        className="text-muted-foreground hover:text-destructive shrink-0 cursor-pointer"
      >
        <span data-i18n="account.cancel">{t("account.cancel")}</span>
      </Button>
    );
  }

  if (!sub.payment && sub.unsubscribeToken) {
    return (
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={isUnsubscribing}
        onClick={() => onUnsubscribe(sub)}
        className="text-orange border-orange/30 hover:bg-orange/10 shrink-0 cursor-pointer"
      >
        {isUnsubscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserMinus className="w-3.5 h-3.5" />}
        <span>{isUnsubscribing ? "Leaving..." : "Unsubscribe"}</span>
      </Button>
    );
  }

  return null;
}

function SubscriptionRow({ sub }: { sub: AccountSubscription }) {
  const { t } = useTranslation();
  const displayName = sub.site?.profile?.name || sub.publisher;
  const avatarUrl = sub.site?.profile?.avatar ? getMediaFullPath(sub.site.profile.avatar) : "";
  const subscribedDate = sub.createdAt ? dayjs(sub.createdAt * 1000).format("MMM D, YYYY") : "";
  const isSupporter = isPaidAccessActive(sub.payment);
  const billingKey =
    sub.payment?.type === "annual" ? "account.annual" : sub.payment?.type === "monthly" ? "account.monthly" : "account.subscription";
  const price =
    typeof sub.payment?.price === "number"
      ? new Intl.NumberFormat("en-US", { style: "currency", currency: sub.payment.currency || "USD" }).format(sub.payment.price / 100)
      : "";
  const until = sub.payment?.endedAt ? dayjs(sub.payment.endedAt * 1000).format("MMM D, YYYY") : "";
  const periodKey = !isSupporter
    ? "account.expired"
    : sub.payment?.canceledAt
      ? until
        ? "account.canceledUntil"
        : "account.canceledPeriod"
      : "account.renewal";

  return (
    <div className="flex items-center gap-3 min-w-0">
      <UserAvatar
        src={avatarUrl}
        name={displayName}
        colorSeed={sub.publisher}
        className="w-11 h-11 rounded-full shrink-0 border border-border/60"
        fallbackClassName="text-sm font-bold"
      />
      <div className="flex flex-col min-w-0">
        <span className="text-xs sm:text-sm font-bold text-foreground truncate">{displayName}</span>
        {subscribedDate ? (
          <span className="text-[11px] leading-relaxed text-muted-foreground" data-i18n="account.subscribedFrom">
            {t("account.subscribedFrom", { date: subscribedDate })}
          </span>
        ) : null}
        {sub.payment ? (
          <>
            <span className="text-[11px] text-muted-foreground">
              <span data-i18n={billingKey}>{t(billingKey)}</span>
              {price ? `: ${price}` : ""}
            </span>
            {!isSupporter || sub.payment.canceledAt || until ? (
              <span className="text-[11px] leading-relaxed text-muted-foreground" data-i18n={periodKey}>
                {t(periodKey, { date: until })}
              </span>
            ) : null}
          </>
        ) : (
          <span className="text-[11px] leading-relaxed text-muted-foreground" data-i18n="account.freeDescription">
            {t("account.freeDescription")}
          </span>
        )}
      </div>
    </div>
  );
}

export function AccountModal({ isOpen, onClose, onSignOut }: AccountModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [subscriptions, setSubscriptions] = useState<AccountSubscription[] | null>(null);
  const [loadError, setLoadError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [unsubscribingToken, setUnsubscribingToken] = useState<string | null>(null);
  const [cancelTarget, setCancelTarget] = useState<AccountSubscription | null>(null);
  const [isCancelingPaid, setIsCancelingPaid] = useState(false);
  const [unsubscribeError, setUnsubscribeError] = useState("");
  const loadAccount = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      onClose();
      if (onSignOut) onSignOut();
      return;
    }

    setIsLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${API_BASE}/account/login/info`, {
        headers: { Authorization: token },
      });
      const account = response.data?.data?.account;
      setEmail(typeof account?.email === "string" && account.email ? account.email : getAuthEmail() || "");
      setSubscriptions(Array.isArray(account?.subscriptions) ? account.subscriptions : []);
    } catch (err: any) {
      if (handleAuthError(err)) {
        onClose();
        if (onSignOut) onSignOut();
        return;
      }
      setLoadError("Failed to load account. Please try again.");
      setSubscriptions([]);
      setEmail(getAuthEmail() || "");
    } finally {
      setIsLoading(false);
    }
    // onClose / onSignOut are parent setState wrappers.
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setUnsubscribeError("");
      setLoadError("");
      setSubscriptions(null);
      return;
    }
    loadAccount();
  }, [isOpen, loadAccount]);

  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    const previousPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPaddingRight;
    };
  }, [isOpen]);

  const handleUnsubscribe = async (item: AccountSubscription) => {
    if (!item.unsubscribeToken) {
      setUnsubscribeError("No unsubscribe token found.");
      return;
    }

    setUnsubscribingToken(item.unsubscribeToken);
    setUnsubscribeError("");
    try {
      await postUnsubscribe(item.unsubscribeToken);
      setSubscriptions((prev) => (prev ? prev.filter((sub) => sub.unsubscribeToken !== item.unsubscribeToken) : prev));
      notifySubscriptionChanged();
    } catch (err: any) {
      setUnsubscribeError(err.response?.data?.error || err.message || "Failed to unsubscribe.");
    } finally {
      setUnsubscribingToken(null);
    }
  };

  const handleConfirmCancelPaid = async () => {
    const token = cancelTarget?.unsubscribeToken;
    if (!cancelTarget || !token) {
      setUnsubscribeError("Cancellation is not available for this subscription.");
      setCancelTarget(null);
      return;
    }

    setIsCancelingPaid(true);
    setUnsubscribeError("");
    try {
      await postUnsubscribe(token);
      const publisher = cancelTarget.publisher;
      setSubscriptions((prev) =>
        prev
          ? prev.map((sub) =>
              sub.publisher === publisher && sub.payment
                ? { ...sub, payment: { ...sub.payment, canceledAt: Math.floor(Date.now() / 1000) } }
                : sub,
            )
          : prev,
      );
      setCancelTarget(null);
      notifySubscriptionChanged();
    } catch (err: any) {
      setUnsubscribeError(err.response?.data?.error || err.message || "Could not cancel this subscription.");
    } finally {
      setIsCancelingPaid(false);
    }
  };

  const handleSignOut = () => {
    removeAuthToken();
    notifySubscriptionChanged();
    onClose();
    if (onSignOut) onSignOut();
  };

  const currentSubscription = useMemo(() => {
    if (!subscriptions) return null;
    return subscriptions.find((sub) => sub.publisher === PublisherConfig.username) || null;
  }, [subscriptions]);
  const showSkeleton = isLoading || (subscriptions === null && !loadError);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-start justify-center p-4 pt-16 sm:pt-24 bg-overlay backdrop-blur-xs overflow-hidden overscroll-none">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Account"
        aria-busy={showSkeleton}
        className="relative w-full max-w-2xl max-h-[calc(100vh-5rem)] sm:max-h-[calc(100vh-7.5rem)] overflow-hidden flex flex-col bg-dialog text-foreground border border-border rounded-md shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col flex-1 min-h-0">
          <div className="pl-5 pr-14 pt-5 pb-6 sm:pl-6 sm:pt-6 shrink-0">
            <h1 className="text-lg font-bold text-foreground">Manage your subscription</h1>
          </div>
          <div className="border-t border-border shrink-0" />

          <section className="flex flex-col flex-1 min-h-0">
            <div className="flex items-start gap-3 px-5 sm:px-6 py-6 shrink-0">
              {showSkeleton ? (
                <Skeleton className="size-8.5 rounded-md shrink-0" />
              ) : (
                <div className="p-2 rounded-md bg-muted text-muted-foreground shrink-0">
                  <Mail className="w-4.5 h-4.5" />
                </div>
              )}
              <div className="min-w-0 space-y-1">
                {showSkeleton ? (
                  <Skeleton className="h-3 w-10" />
                ) : (
                  <h2 className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Email</h2>
                )}
                {showSkeleton ? (
                  <Skeleton className="h-5 w-44" />
                ) : (
                  <p className="text-sm font-semibold text-foreground break-all">{email || "-"}</p>
                )}
                {showSkeleton ? (
                  <Skeleton className="h-3.5 w-72 max-w-full" />
                ) : (
                  <p className="text-[11px] text-muted-foreground font-medium">You signed in with this email. It cannot be changed.</p>
                )}
              </div>
            </div>

            <div className="px-5 sm:px-6 pb-6 flex flex-col flex-1 min-h-0">
              {showSkeleton ? (
                <div className="flex items-center gap-3 min-w-0" role="status" aria-label="Loading subscription">
                  <Skeleton className="w-11 h-11 rounded-full shrink-0" />
                  <div className="flex flex-col gap-1.5 min-w-0">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3.5 w-64 max-w-full" />
                    <Skeleton className="h-3.5 w-52 max-w-full" />
                  </div>
                </div>
              ) : loadError ? (
                <p className="text-xs text-destructive font-medium">{loadError}</p>
              ) : !currentSubscription ? (
                <div className="flex flex-col items-center justify-center text-center py-2">
                  <div className="p-3.5 rounded-full bg-muted/50 border border-border text-muted-foreground mb-3">
                    <Mail className="w-6 h-6" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground mb-1" data-i18n="account.emptyTitle">
                    {t("account.emptyTitle")}
                  </h3>
                  <p className="text-xs text-muted-foreground max-w-sm leading-relaxed" data-i18n="account.emptyDescription">
                    {t("account.emptyDescription")}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-4 min-h-0">
                  {unsubscribeError ? <p className="text-[11px] text-destructive shrink-0">{unsubscribeError}</p> : null}
                  <div className="overflow-y-auto overscroll-contain">
                    <SubscriptionRow sub={currentSubscription} />
                  </div>
                </div>
              )}
            </div>
          </section>

          <div className="border-t border-border shrink-0" />
          <div className="px-5 sm:px-6 py-4 flex items-center justify-between gap-3 shrink-0">
            <div>
              {showSkeleton ? (
                <Skeleton className="h-8 w-28" />
              ) : currentSubscription ? (
                <SubscriptionAction
                  sub={currentSubscription}
                  unsubscribingToken={unsubscribingToken}
                  onUnsubscribe={handleUnsubscribe}
                  onCancelPaid={(item) => {
                    setUnsubscribeError("");
                    setCancelTarget(item);
                  }}
                />
              ) : null}
            </div>
            <Button type="button" variant="outline" size="sm" onClick={handleSignOut} className="shrink-0 cursor-pointer">
              <LogOut className="w-3.5 h-3.5" />
              Sign out
            </Button>
          </div>
        </div>
      </div>

      {cancelTarget ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center p-4 bg-overlay">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Cancel subscription"
            className="w-full max-w-sm rounded-md border border-border bg-dialog text-foreground p-5 shadow-xl text-left"
          >
            <h2 className="text-base font-bold text-foreground" data-i18n="account.cancel">
              {t("account.cancel")}?
            </h2>
            <p className="mt-2 text-xs text-muted-foreground leading-relaxed" data-i18n="account.cancelDescription">
              {t("account.cancelDescription", { name: cancelTarget.site?.profile?.name || cancelTarget.publisher })}
            </p>
            <div className="mt-5 flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 cursor-pointer"
                disabled={isCancelingPaid}
                onClick={() => setCancelTarget(null)}
              >
                <span data-i18n="account.keep">{t("account.keep")}</span>
              </Button>
              <Button
                type="button"
                className="flex-1 cursor-pointer bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isCancelingPaid}
                onClick={handleConfirmCancelPaid}
              >
                {isCancelingPaid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                <span data-i18n={isCancelingPaid ? "account.canceling" : "account.cancel"}>
                  {t(isCancelingPaid ? "account.canceling" : "account.cancel")}
                </span>
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
